import {SyncableStore} from "@/stores/plugins/sync/types";

const syncedStores = new Map<string, SyncableStore>();

/**
 * Sets up the master window synchronization by subscribing to store updates
 * and sending the current state of the store to the main process.
 */
export const setupMasterWindowSync = (store: SyncableStore) => {
    syncedStores.set(store.$id, store);

    store.$subscribe(() => {
        if (store._isSyncing) {
            return;
        }

        try {
            window.ipcRenderer?.sendStoreUpdate({
                storeId: store.$id,
                state: store.getSyncState!(),
                timestamp: Date.now()
            });
        } catch (error) {
            throw new Error(`Failed to send store update for ${store.$id}: ${error}`);
        }
    });

    setupSyncRequestHandler();
};

/**
 * Sets up the sync request handler for the master window.
 */
const setupSyncRequestHandler = (): void => {
    if (window._syncRequestHandlerSetup) {
        return;
    }

    window._syncRequestHandlerSetup = true;

    window.ipcRenderer?.onSyncRequest((storeIds: string[]) => {
        storeIds.forEach(id => {
            sendStoreState(id, true);
        });
    });
};

/**
 * Sends the current state of the store to the main process.
 *
 * @param storeId - The ID of the store to send.
 * @param isInitialSync - Whether this is an initial sync request.
 */
const sendStoreState = (storeId: string, isInitialSync = false): void => {
    const store = syncedStores.get(storeId);

    if (!store || !store.getSyncState) {
        return;
    }

    const syncState = store.getSyncState();

    window.ipcRenderer?.sendStoreUpdate({
        storeId,
        state: syncState,
        timeStamp: Date.now(),
        isInitialSync
    });
};

export const getMasterWindowSyncedStores = (): Map<string, SyncableStore> => {
    return syncedStores;
};
